import { lazy, Suspense } from "solid-js";
import { render } from "solid-js/web";
import { GAMES } from "./brand.ts";
import { carryOver } from "./carryOver.ts";
import { count, startCounting } from "./count.ts";
import { resolveGamesOrigin, sharedGameId } from "./gameStore.ts";
import { Home } from "./Home.tsx";
import { registerServiceWorker } from "./pwa.ts";
import { isDisplayWindow } from "./screenLink.ts";
import { sunoKind } from "./suno.ts";
import { isTrackLink } from "./track.ts";
import { soundcloudTrack } from "./soundcloud.ts";
import { youtubeId } from "./youtube.ts";

/*
 * Everything but the front door, fetched when it is asked for.
 *
 * The renderer is most of the weight here, and five of these pull it in. Loaded together they
 * made everybody wait on Three.js before reading a sentence, including the people who only came
 * to see what somebody sent them. Home stays eager because it is what arriving means.
 *
 * The game pages hang off a constant the bundler can fold, so the visualizer build does not
 * merely hide them: it is compiled without them, and the chunks are never written.
 */
const App = lazy(() => import("./App.tsx").then((module) => ({ default: module.App })));
const Display = lazy(() => import("./Display.tsx").then((module) => ({ default: module.Display })));
const Haptics = lazy(() => import("./Haptics.tsx").then((module) => ({ default: module.Haptics })));
const Privacy = lazy(() => import("./Privacy.tsx").then((module) => ({ default: module.Privacy })));

const Catalog = GAMES
  ? lazy(() => import("./Catalog.tsx").then((module) => ({ default: module.Catalog })))
  : App;
const Forge = GAMES
  ? lazy(() => import("./Forge.tsx").then((module) => ({ default: module.Forge })))
  : App;
const Maker = GAMES
  ? lazy(() => import("./Maker.tsx").then((module) => ({ default: module.Maker })))
  : App;
const Play = GAMES
  ? lazy(() => import("./Play.tsx").then((module) => ({ default: module.Play })))
  : App;
const Scoring = GAMES
  ? lazy(() => import("./Scoring.tsx").then((module) => ({ default: module.Scoring })))
  : App;
const Queue = GAMES
  ? lazy(() => import("./Queue.tsx").then((module) => ({ default: module.Queue })))
  : App;
const Sets = GAMES
  ? lazy(() => import("./Sets.tsx").then((module) => ({ default: module.Sets })))
  : App;
const Shelf = GAMES
  ? lazy(() => import("./Shelf.tsx").then((module) => ({ default: module.Shelf })))
  : App;
const Watch = GAMES
  ? lazy(() => import("./Watch.tsx").then((module) => ({ default: module.Watch })))
  : App;
import "./styles.css";

const root = document.getElementById("root");
if (root === null) throw new Error("Missing #root");

// Before anything reads a setting, so nobody arrives to an app that has forgotten them.
carryOver();

/*
 * One bundle, several ways in.
 *
 * A game id is somebody else's track to play, a song link is one that has not been made into a
 * game yet, a profile is a shelf of them, and the second window shares the code but not the
 * interface so a projector shows the work and nothing else.
 *
 * Arriving with nothing gets the front door rather than the studio. The studio is still the
 * whole thing and is one tap away, but it is a control room, and somebody who was told about a
 * game should not have to find the game inside it.
 */
function route() {
  const search = new URLSearchParams(location.search);
  startCounting(resolveGamesOrigin());
  count("arrive");

  /*
   * A build with no games in it is a visualizer, and there is only the one screen.
   *
   * Answered before anything reads the address, so an old link, a share sheet or a bookmark
   * from the other product cannot reach a page this build does not have.
   */
  if (!GAMES) {
    if (search.has("privacy")) return <Privacy />;
    if (isDisplayWindow()) return <Display />;
    return <App />;
  }

  /*
   * Arriving from another app's share sheet.
   *
   * A share is the shortest road there is from hearing something to making a game of it, and it
   * is the road a phone actually offers. What comes over is rarely tidy: most apps send a line
   * of prose with the address somewhere inside it rather than filling in the url field, so the
   * link has to be found rather than read.
   */
  const shared = `${search.get("url") ?? ""} ${search.get("text") ?? ""}`.trim();
  if (shared !== "") {
    const found = /https?:\/\/\S+/.exec(shared);
    if (found !== null) {
      count("paste");
      location.replace(
        `${location.origin}${location.pathname}?suno=${encodeURIComponent(found[0])}`,
      );
      return null;
    }
  }

  // Whether a phone can be made to buzz is a question only a phone can answer.
  if (search.has("haptics")) return <Haptics />;
  if (search.has("privacy")) return <Privacy />;
  if (search.has("scoring")) return <Scoring />;
  if (search.has("queue")) return <Queue />;
  if (sharedGameId() !== null) {
    count("play-open");
    return <Play />;
  }

  // A shelf of somebody's games, and the sets they put them in.
  if (search.has("sets") || search.has("set")) {
    return <Sets open={search.get("set") ?? ""} />;
  }

  // Everybody's, rather than yours: the games people chose to put out.
  if (search.has("made")) return <Shelf />;

  // One person's, which is what a name on a shelf ought to lead to.
  const under = search.get("by") ?? "";
  if (under !== "") return <Maker name={under} />;

  const pasted = search.get("suno") ?? "";
  const kind = sunoKind(pasted);
  if (kind === "profile") {
    count("shelf-open");
    return <Catalog link={pasted} />;
  }

  // Neither of these can be read, only listened to, so both take the same longer road.
  const watching = youtubeId(pasted);
  if (watching !== null) {
    return <Watch source={{ kind: "youtube", id: watching }} name={search.get("t") ?? ""} />;
  }
  const heard = soundcloudTrack(pasted);
  if (heard !== null) {
    return <Watch source={{ kind: "soundcloud", url: heard }} name={search.get("t") ?? ""} />;
  }

  if (isTrackLink(pasted)) {
    /*
     * Corrections a listener can make and the analysis cannot.
     *
     * Which beat is the one is a matter of a quarter of a bar, and whether a record is counted at
     * 85 or 170 is a real ambiguity that no amount of further processing settles, because both
     * are true of the signal. Somebody tapping their foot has already decided both, so they are
     * arguments here rather than a better algorithm.
     */
    const shift = Number(search.get("beat") ?? "0");
    const scale = Number(search.get("tempo") ?? "1");
    const fix = {
      ...(Number.isInteger(shift) && shift !== 0 ? { beatShift: shift } : {}),
      ...(scale === 0.5 || scale === 2 ? { tempoScale: scale as 0.5 | 2 } : {}),
    };
    return <Forge link={pasted} name={search.get("t") ?? ""} fix={fix} />;
  }

  if (isDisplayWindow()) return <Display />;
  return search.has("studio") ? <App /> : <Home />;
}

// Nothing but the background while a page arrives: a spinner for a fetch this short is noise.
render(() => <Suspense fallback={<div class="app app--bare" />}>{route()}</Suspense>, root);
registerServiceWorker();
